import store from '../redux/store';
import { updateFileProgress, updateFileStatus, removeUploadSession, setUploadError, setCompletedUploadSummary } from '../redux/slices/uploadProgressSlice';
import { initUploadChecksum, startBatchUpload, uploadChunk, batchCompleteUpload, splitFileIntoChunks, completeDatasetAPI, fetchUploadedFiles } from '../redux/slices/uploadSlice';
import { UPLOAD_STATUS, DATASET_STATUS } from '../constants/uploadStatus';
import { MAX_PARALLEL_UPLOADS, MAX_PARALLEL_FILES, BATCH_SIZE, AUTO_RETRY_CONFIG, RETRY_CONFIG, MAX_URL_REFRESHES } from '../constants/uploadConfig';
import { 
    setUploadAbortController, 
    removeUploadAbortController, 
    setUploadFileObjects,
    getUploadFileObjects,
    removeUploadFileObjects,
    setUploadSessionData,
    addFileSessionData,
    removeUploadSessionData,
    setUploadContext,
    removeUploadContext
} from '../services/uploadAbortControllers';
import { addErrorNotification, addInfoNotification } from '../redux/slices/notificationSlice';
import { computeFilePartChecksums } from '../utils/checksumUtils';
import { detectFileType } from '../utils/fileTypeDetector';
import logger from '../utils/logger';
import { DEFAULT_PAGE_LIMIT } from '../constants/apiEndpoints';
import { resolveFileExtension } from '../utils/fileExtensionUtils';

// Helper function to split files into batches
const splitIntoBatches = (files, batchSize = BATCH_SIZE) => {
    const batches = [];
    for (let i = 0; i < files.length; i += batchSize) {
        batches.push(files.slice(i, i + batchSize));
    }
    return batches;
};

// Helper function to detect file lock/access errors
const isFileLockError = (error) => {
    if (!error) return false;
    
    // Browser file APIs throw DOMException with specific error names for file access issues
    const errorName = error.name || '';
    
    // Check for standard DOMException names that indicate file access problems:
    // - NotReadableError: File cannot be read (Chrome/Firefox) - most common for locked files
    // - NotFoundError: File not accessible (Safari) - can also mean locked file
    return errorName === 'NotReadableError' || errorName === 'NotFoundError';
};

// Helper: Check if error is a URL expiry error
const isUrlExpiredError = (error) => {
    if (!error) return false;
    if (error.type === 'URL_EXPIRED') return true;
    if (typeof error === 'string' && error.includes('URL_EXPIRED')) return true;
    if (error.message && error.message.includes('Presigned URL expired')) return true;
    return false;
};

// Helper: Refresh URLs by calling Phase 2 API again for incomplete files
const refreshExpiredUrls = async (uploadId, filesForRefresh, uploadContext, dispatch) => {
    logger.info(`🔄 Refreshing expired URLs for ${filesForRefresh.length} file(s)...`);
    
    const state = store.getState();
    const upload = state.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
    const datasetName = upload?.datasetName || '';
    const contractName = upload?.contract?.label || uploadContext.contractName || '';

    const result = await dispatch(startBatchUpload({
        dataset_name: datasetName,
        contract_name: contractName,
        business_unit: uploadContext.businessUnit,
        project: uploadContext.project,
        data_provider_name: uploadContext.dataProviderName,
        files: filesForRefresh
    })).unwrap();

    logger.info(`✅ URL refresh complete: got ${Array.isArray(result) ? result.length : 0} result(s)`);
    return Array.isArray(result) ? result : [result];
};

// Helper function to check abort/pause status
const checkAbortOrPause = (abortSignal, uploadId, context = '') => {
    if (!abortSignal?.aborted) return null;
    
    const currentState = store.getState();
    const currentUpload = currentState.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
    const isPaused = currentUpload && currentUpload.status === UPLOAD_STATUS.PAUSED;
    
    if (isPaused) {
        logger.info(`⏸️  Upload paused ${context}`);
        return { wasAborted: false, overallStatus: UPLOAD_STATUS.PAUSED };
    } else {
        logger.info(`🛑 Upload aborted ${context}`);
        return { wasAborted: true };
    }
};

// Helper function to initialize a batch
const initializeBatch = async (batchNumber, totalBatches, currentBatch, uploadId, uploadContext) => {
    const dispatch = store.dispatch;
    
    // Show batch initialization progress
    dispatch(addInfoNotification({
        message: `Initializing upload batch ${batchNumber}/${totalBatches} (${currentBatch.length} files)...`
    }));

    // Prepare file metadata for THIS batch
    const filesData = await Promise.all(currentBatch.map(async (file) => {
        const fileName = file.name;
        const actualFile = file.file || file;
        const extension = await resolveFileExtension(fileName, actualFile);
        
        const filePath = file.relativePath || file.name;
        return {
            file_name: fileName,
            extension: extension,
            file_size: file.size,
            file_path: filePath,
            content_type: file.file?.type || 'application/octet-stream'
        };
    }));

    // Get dataset and contract names from Redux state
    const state = store.getState();
    const upload = state.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
    const datasetName = upload?.datasetName || '';
    const contractName = upload?.contract?.label || uploadContext.contractName || '';

    logger.info(`📡 Calling initUploadChecksum API (Phase 1) for batch ${batchNumber}/${totalBatches}:`, {
        dataset_name: datasetName,
        contract_name: contractName,
        filesCount: filesData.length,
        batchNumber,
        totalBatches,
        uploadContext
    });

    const startUploadAbortController = new AbortController();
    setUploadAbortController(`${uploadId}__start_batch_${batchNumber}`, startUploadAbortController);

    try {
        const batchResult = await dispatch(initUploadChecksum({
            dataset_name: datasetName,
            contract_name: contractName,
            business_unit: uploadContext.businessUnit,
            project: uploadContext.project,
            data_provider_name: uploadContext.dataProviderName,
            files: filesData,
            _signal: startUploadAbortController.signal
        })).unwrap();

        removeUploadAbortController(`${uploadId}__start_batch_${batchNumber}`);
        logger.info(`✅ Batch ${batchNumber}/${totalBatches} initialization complete - received ${Array.isArray(batchResult) ? batchResult.length : 1} upload configs`);
        
        return { success: true, result: batchResult };
    } catch (batchInitError) {
        removeUploadAbortController(`${uploadId}__start_batch_${batchNumber}`);
        return { success: false, error: batchInitError };
    }
};

// Helper function to process files for Phase 2
const processFilesForPhase2 = async (filesToProcess, uploadId, batchNumber, totalBatches, abortSignal) => {
    const dispatch = store.dispatch;
    const filesNeedingChecksums = filesToProcess.filter(({ detail }) => detail?.checksum_algorithm);
    const filesWithoutChecksums = filesToProcess.filter(({ detail }) => !detail?.checksum_algorithm);
    
    logger.info(`   - ${filesNeedingChecksums.length} files need checksum computation`);
    logger.info(`   - ${filesWithoutChecksums.length} files don't need checksums`);
    
    // Update status to computing checksums for files that need it
    filesNeedingChecksums.forEach(({ file }) => {
        dispatch(updateFileStatus({
            uploadId,
            fileId: file.id,
            status: UPLOAD_STATUS.COMPUTING_CHECKSUMS
        }));
    });

    const filesForPhase2 = [];
    
    // Compute checksums for files that have an algorithm
    for (const { file, detail } of filesNeedingChecksums) {
        try {
            logger.info(`🔐 Computing checksums for file: ${file.name}`);
            
            const algorithm = detail.checksum_algorithm || detail.algorithm || 'SHA-256';
            const chunkSize = detail.chunk_size || 10 * 1024 * 1024;
            const actualFile = file.file || file;
            
            logger.info(`🔐 File details:`, {
                fileName: file.name,
                fileSize: actualFile.size,
                chunkSize,
                algorithm,
                totalParts: Math.ceil(actualFile.size / chunkSize)
            });
            
            const checksums = await computeFilePartChecksums(
                actualFile,
                chunkSize,
                algorithm,
                (currentPart, totalParts) => {
                    const progress = Math.round((currentPart / totalParts) * 100);
                    logger.debug(`📊 Checksum progress for ${file.name}: ${currentPart}/${totalParts} (${progress}%)`);
                    dispatch(updateFileProgress({
                        uploadId,
                        fileId: file.id,
                        progress,
                        status: UPLOAD_STATUS.COMPUTING_CHECKSUMS
                    }));
                }
            );

            logger.debug(`✅ Computed ${checksums.length} checksums for ${file.name}`);
            logger.debug(`📋 First checksum sample:`, checksums[0]);

            const fileName = file.name;
            const extension=await resolveFileExtension(fileName, actualFile);
           
            
            const filePath = file.relativePath || file.name;
            const contentType = actualFile.type || 'application/octet-stream';
            
            const phase2Payload = {
                file_name: fileName,
                extension: extension,
                file_size: actualFile.size,
                file_path: filePath,
                content_type: contentType,
                upload_id: detail.upload_id,
                parts: checksums.map(part => ({
                    part_number: part.part_number,
                    checksum: part.checksum
                }))
            };
            
            logger.debug(`📤 Phase 2 payload for ${fileName}:`, {
                ...phase2Payload,
                parts: `${phase2Payload.parts.length} parts`
            });
            
            filesForPhase2.push(phase2Payload);

        } catch (checksumError) {
            console.error(`❌ Failed to compute checksums for ${file.name}:`, checksumError);
            dispatch(updateFileStatus({
                uploadId,
                fileId: file.id,
                status: UPLOAD_STATUS.FAILED,
                error: `Checksum computation failed: ${checksumError.message}`
            }));
            continue;
        }

        // Check for abort/pause during checksum computation
        const abortResult = checkAbortOrPause(abortSignal, uploadId, `during checksum computation (batch ${batchNumber}/${totalBatches})`);
        if (abortResult) return abortResult;
    }
    
    // Add files that don't need checksums
    for (const { file, detail } of filesWithoutChecksums) {
        const fileName = file.name;
        const actualFile = file.file || file;
        
       const extension=await resolveFileExtension(fileName, actualFile);
        
        
        const filePath = file.relativePath || file.name;
        const contentType = actualFile.type || 'application/octet-stream';
        
        filesForPhase2.push({
            file_name: fileName,
            extension: extension,
            file_size: actualFile.size,
            file_path: filePath,
            content_type: contentType,
            upload_id: detail.upload_id
        });
    }
    
    return { filesForPhase2, filesNeedingChecksums, filesWithoutChecksums };
};

// Helper function to handle auto-retry logic
const handleAutoRetry = async (uploadId, datasetId, files, uploadContext, abortSignal, isRetryCall, wasAborted, lockedFiles) => {
    if (wasAborted || isRetryCall || !AUTO_RETRY_CONFIG.enabled) return;
    
    const state = store.getState();
    const currentUpload = state.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
    
    if (!currentUpload) return;
    
    const failedFiles = currentUpload.files.filter(f => 
        f.status === UPLOAD_STATUS.FAILED || 
        f.status === UPLOAD_STATUS.PARTIALLY_FAILED
    );
    
    const retryableFiles = AUTO_RETRY_CONFIG.retryLockedFiles 
        ? failedFiles 
        : failedFiles.filter(f => !lockedFiles.includes(f.name));
    
    if (retryableFiles.length === 0) return;
    
    logger.info(`\n${'🔄'.repeat(40)}`);
    logger.info(`🔄 AUTO-RETRY: Found ${retryableFiles.length} failed file(s) eligible for retry`);
    logger.info(`   Max attempts: ${AUTO_RETRY_CONFIG.maxAttempts}`);
    logger.info(`   Delay: ${AUTO_RETRY_CONFIG.delayMs}ms`);
    logger.info(`   Files: ${retryableFiles.map(f => f.name).join(', ')}`);
    logger.info(`${'🔄'.repeat(40)}\n`);
    
    const retryFileObjects = retryableFiles.map(f => {
        const storedFiles = getUploadFileObjects(uploadId);
        const storedFile = storedFiles?.find(sf => sf.id === f.id);
        return {
            id: f.id,
            name: f.name,
            size: f.size,
            relativePath: f.relativePath,
            file: storedFile?.file
        };
    }).filter(f => f.file);
    
    if (retryFileObjects.length === 0) {
        console.warn('⚠️ No file objects available for retry (may have been lost after page reload)');
        return;
    }
    
    for (let attempt = 1; attempt <= AUTO_RETRY_CONFIG.maxAttempts; attempt++) {
        logger.info(`\n🔄 AUTO-RETRY ATTEMPT ${attempt}/${AUTO_RETRY_CONFIG.maxAttempts} for ${retryFileObjects.length} file(s)`);
        
        if (AUTO_RETRY_CONFIG.delayMs > 0 && attempt > 1) {
            logger.info(`⏳ Waiting ${AUTO_RETRY_CONFIG.delayMs}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, AUTO_RETRY_CONFIG.delayMs));
        }
        
        retryFileObjects.forEach(f => {
            store.dispatch(updateFileStatus({
                uploadId,
                fileId: f.id,
                status: UPLOAD_STATUS.PENDING,
                error: undefined
            }));
        });
        
        logger.info(`🚀 Starting retry upload for ${retryFileObjects.length} file(s)...`);
        try {
            await orchestrateUpload(
                uploadId,
                datasetId,
                retryFileObjects,
                uploadContext,
                abortSignal,
                true
            );
            
            const retryState = store.getState();
            const retryUpload = retryState.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
            
            if (retryUpload) {
                const stillFailed = retryUpload.files.filter(f => 
                    retryFileObjects.some(rf => rf.id === f.id) &&
                    (f.status === UPLOAD_STATUS.FAILED || f.status === UPLOAD_STATUS.PARTIALLY_FAILED)
                );
                
                if (stillFailed.length === 0) {
                    logger.info(`✅ AUTO-RETRY SUCCESS: All ${retryFileObjects.length} file(s) uploaded successfully on attempt ${attempt}`);
                    break;
                } else if (attempt < AUTO_RETRY_CONFIG.maxAttempts) {
                    logger.info(`⚠️ AUTO-RETRY: ${stillFailed.length} file(s) still failed, will retry again (attempt ${attempt + 1}/${AUTO_RETRY_CONFIG.maxAttempts})`);
                } else {
                    logger.info(`❌ AUTO-RETRY EXHAUSTED: ${stillFailed.length} file(s) still failed after ${AUTO_RETRY_CONFIG.maxAttempts} attempt(s)`);
                }
            }
        } catch (retryError) {
            console.error(`❌ AUTO-RETRY attempt ${attempt} failed:`, retryError);
            if (attempt >= AUTO_RETRY_CONFIG.maxAttempts) {
                logger.info(`❌ AUTO-RETRY EXHAUSTED: Max attempts reached`);
            }
        }
    }
};
// Helper function to build upload summary object
const buildUploadSummary = (summaryUpload, wasAborted) => {
    const completedFiles = summaryUpload.files.filter(f => f.status === UPLOAD_STATUS.COMPLETED);
    const failedFilesArr = summaryUpload.files.filter(f => 
        f.status === UPLOAD_STATUS.FAILED || f.status === UPLOAD_STATUS.PARTIALLY_FAILED
    );
    const skippedFilesFromUpload = summaryUpload.skippedFilesCount || 0;

    const totalSize = summaryUpload.files.reduce((sum, f) => sum + (f.size || 0), 0);
    const uploadedSize = summaryUpload.files.reduce((sum, f) => sum + (f.uploadedBytes || 0), 0);

    const endTime = new Date().toISOString();
    const startTime = summaryUpload.startedAt;
    const durationMs = new Date(endTime) - new Date(startTime);
    const durationSeconds = Math.round(durationMs / 1000);

    // Use canonical constants for overallStatus (no literal strings)
    let overallStatus;
    if (wasAborted) {
        overallStatus = DATASET_STATUS.UPLOAD_ABORTED;
    } else if (summaryUpload.status === UPLOAD_STATUS.FAILED) {
        overallStatus = UPLOAD_STATUS.FAILED;
    } else if (summaryUpload.status === UPLOAD_STATUS.PARTIALLY_FAILED) {
        overallStatus = UPLOAD_STATUS.PARTIALLY_FAILED;
    } else {
        overallStatus = DATASET_STATUS.UPLOAD_COMPLETED;
    }

    return {
        uploadId: summaryUpload.uploadId,
        datasetName: summaryUpload.datasetName,
        status: overallStatus,
        totalFiles: summaryUpload.files.length + skippedFilesFromUpload,
        successfulFiles: completedFiles.length,
        failedFiles: failedFilesArr.length,
        skippedFiles: skippedFilesFromUpload,
        totalSize,
        uploadedSize,
        startedAt: startTime,
        completedAt: endTime,
        durationSeconds,
    };
};

// Main orchestrator function
export const orchestrateUpload = async (uploadId, datasetId, files, uploadContext, abortSignal = null, isRetryCall = false) => {
    logger.info(`📤 orchestrateUpload started for uploadId: ${uploadId}, total files: ${files.length}, isRetryCall: ${isRetryCall}`);
    const dispatch = store.dispatch;
    let wasAborted = false;
    
    // Track files that failed due to lock/access errors
    const lockedFiles = [];

    // Split files into batches
    const batches = splitIntoBatches(files, BATCH_SIZE);
    const totalBatches = batches.length;
    
    logger.info(`📦 Split ${files.length} files into ${totalBatches} batch${totalBatches > 1 ? 'es' : ''} of max ${BATCH_SIZE} files each`);
    
    // Show info notification for large uploads
    if (totalBatches > 1) {
        dispatch(addInfoNotification({
            message: `Processing ${files.length} files in ${totalBatches} batches for optimal performance`
        }));
    }

    try {
        // Initialize session data storage for this upload
        setUploadSessionData(uploadId, []);
        
        // Store upload context for pause/resume functionality
        setUploadContext(uploadId, uploadContext);

        // Process each batch sequentially
        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
            const currentBatch = batches[batchIndex];
            const batchNumber = batchIndex + 1;
            
            logger.info(`\n${'='.repeat(80)}`);
            logger.info(`📦 BATCH ${batchNumber}/${totalBatches}: Processing ${currentBatch.length} files`);
            logger.info(`   Files in this batch: ${currentBatch[0].name} ... ${currentBatch[currentBatch.length - 1].name}`);
            logger.info(`${'='.repeat(80)}\n`);

            // Check abort signal at the start of each batch
            const abortResult = checkAbortOrPause(abortSignal, uploadId, `before starting batch ${batchNumber}/${totalBatches}`);
            if (abortResult) {
                if (!abortResult.wasAborted) {
                    // Paused - just return
                    return abortResult;
                } else {
                    // Aborted - mark remaining files as failed
                    for (let i = batchIndex; i < totalBatches; i++) {
                        batches[i].forEach(file => {
                            dispatch(updateFileStatus({
                                uploadId,
                                fileId: file.id,
                                status: UPLOAD_STATUS.FAILED,
                                error: 'Upload aborted by user'
                            }));
                        });
                    }
                    wasAborted = true;
                    return { wasAborted };
                }
            }

            const initResult = await initializeBatch(batchNumber, totalBatches, currentBatch, uploadId, uploadContext);
            
            let batchResult;
            let batchInitFailed = false;
            
            if (!initResult.success) {
                // Check if this was an abort before processing as a failure
                const abortResult = checkAbortOrPause(abortSignal, uploadId, `during batch ${batchNumber}/${totalBatches} initialization`);
                if (abortResult) return abortResult;
                
                // Not an abort - this is a real failure
                batchInitFailed = true;
                console.error(`❌ Batch ${batchNumber}/${totalBatches} initialization FAILED:`, initResult.error);
                
                dispatch(addErrorNotification({
                    message: `Failed to initialize batch ${batchNumber}/${totalBatches}: ${initResult.error.message || 'Unknown error'}. Continuing with remaining batches...`
                }));
                
                // Mark all files in this batch as failed
                currentBatch.forEach(file => {
                    dispatch(updateFileStatus({
                        uploadId,
                        fileId: file.id,
                        status: UPLOAD_STATUS.FAILED,
                        error: `Batch initialization failed: ${initResult.error.message || 'Unknown error'}`
                    }));
                });
                
                logger.info(`➡️  Continuing to next batch despite batch ${batchNumber} failure...`);
                continue;
            }
            
            batchResult = initResult.result;

            // Skip upload processing if batch init failed
            if (batchInitFailed) {
                continue;
            }

            // Check if aborted or paused after batch initialization
             /* istanbul ignore start */
            const abortResultAfterInit = checkAbortOrPause(abortSignal, uploadId, `after batch ${batchNumber}/${totalBatches} initialization`);
            if (abortResultAfterInit) {
                if (abortResultAfterInit.wasAborted) wasAborted = true;
                return abortResultAfterInit;
            }
            /* istanbul ignore stop */

            // Backend returns an array of upload initialization data, one per file
            let uploadInitDataArray = Array.isArray(batchResult) ? batchResult : [batchResult];

            // --- TWO-PHASE CHECKSUM WORKFLOW ---
            // Phase 1 (init-upload-checksum) returns configuration only, no URLs
            // Now we need to: compute checksums (if algorithm provided) and call Phase 2 to get URLs
            logger.info(`🔍 Processing ${currentBatch.length} files after Phase 1 initialization...`);
            logger.info(`📋 API response count: ${uploadInitDataArray.length}`);
            
            // Check if aborted after Phase 1 before processing files
            const abortResult1 = checkAbortOrPause(abortSignal, uploadId, `after Phase 1, before file processing (batch ${batchNumber}/${totalBatches})`);
            if (abortResult1) {
                if (abortResult1.wasAborted) wasAborted = true;
                return abortResult1;
            }
            
            const filesToProcess = [];
            let filesForPhase2 = [];
            currentBatch.forEach((file, idx) => {
                const detail = uploadInitDataArray[idx];
                logger.debug(`📄 File ${idx + 1}/${currentBatch.length}: ${file.name}`, {
                    hasDetail: !!detail,
                    algorithm: detail?.checksum_algorithm,
                    uploadId: detail?.upload_id,
                    isAlreadyUploaded: detail?.is_already_uploaded
                });
                // Phase 1 response never includes part_uploads or urls
                // Process all non-uploaded files
                if (!detail?.is_already_uploaded) {
                    filesToProcess.push({ file, detail, idx });
                }
            });

            if (filesToProcess.length > 0) {
                logger.info(`🔐 Phase 1 complete: Processing ${filesToProcess.length} files`);
                
                const processResult = await processFilesForPhase2(filesToProcess, uploadId, batchNumber, totalBatches, abortSignal);
                if (processResult.wasAborted !== undefined) return processResult;
                
                const { filesForPhase2: _filesForPhase2, filesNeedingChecksums, filesWithoutChecksums } = processResult;
                filesForPhase2 = _filesForPhase2;   
                
                // Check if aborted after checksums before Phase 2
                const abortResult3 = checkAbortOrPause(abortSignal, uploadId, `after checksums, before Phase 2 (batch ${batchNumber}/${totalBatches})`);
                if (abortResult3) {
                    if (abortResult3.wasAborted) wasAborted = true;
                    return abortResult3;
                }

                // Phase 2: Make API call to get pre-signed URLs (with or without checksums)
                if (filesForPhase2.length > 0) {
                    logger.info(`🔐 Phase 2: Sending ${filesForPhase2.length} files to get pre-signed URLs`);
                    logger.info(`   - ${filesNeedingChecksums.length} with checksums`);
                    logger.info(`   - ${filesWithoutChecksums.length} without checksums`);
                    
                    try {
                        // Get dataset and contract names from Redux state
                        const state = store.getState();
                        const upload = state.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
                        const datasetName = upload?.datasetName || '';
                        const contractName = upload?.contract?.label || uploadContext.contractName || '';

                        // Create another AbortController for Phase 2 API call
                        const phase2AbortController = new AbortController();
                        setUploadAbortController(`${uploadId}__start_batch_${batchNumber}_phase2`, phase2AbortController);

                        const phase2Result = await dispatch(startBatchUpload({
                            dataset_name: datasetName,
                            contract_name: contractName,
                            business_unit: uploadContext.businessUnit,
                            project: uploadContext.project,
                            data_provider_name: uploadContext.dataProviderName,
                            files: filesForPhase2,
                            _signal: phase2AbortController.signal
                        })).unwrap();

                        removeUploadAbortController(`${uploadId}__start_batch_${batchNumber}_phase2`);

                        // Merge Phase 2 results back into uploadInitDataArray
                        uploadInitDataArray = [...uploadInitDataArray];
                        const phase2Array = Array.isArray(phase2Result) ? phase2Result : [phase2Result];
                        filesToProcess.forEach(({ idx }, phase2Idx) => {
                            if (phase2Array[phase2Idx]) {
                                uploadInitDataArray[idx] = phase2Array[phase2Idx];
                            }
                        });

                        logger.info(`✅ Phase 2 complete: Received pre-signed URLs with checksum validation`);
                        
                        // Now that Phase 2 succeeded, mark all files in this batch as UPLOADING
                        filesToProcess.forEach(({ file }) => {
                            dispatch(updateFileStatus({
                                uploadId,
                                fileId: file.id,
                                status: UPLOAD_STATUS.UPLOADING
                            }));
                        });

                    } catch (phase2Error) {
                        removeUploadAbortController(`${uploadId}__start_batch_${batchNumber}_phase2`);
                        
                         // Check if this was an abort before processing as a failure
                        const abortResult4 = checkAbortOrPause(abortSignal, uploadId, `during batch ${batchNumber}/${totalBatches} Phase 2`);
                        if (abortResult4) {
                            if (abortResult4.wasAborted) wasAborted = true;
                            return abortResult4;
                        }
                        
                        // Not an abort - this is a real failure
                        console.error(`❌ Phase 2 failed:`, phase2Error);
                        
                        dispatch(addErrorNotification({
                            message: `Failed to get pre-signed URLs with checksums: ${phase2Error.message || 'Unknown error'}`
                        }));

                        // Mark files as failed
                        filesToProcess.forEach(({ file }) => {
                            dispatch(updateFileStatus({
                                uploadId,
                                fileId: file.id,
                                status: UPLOAD_STATUS.FAILED,
                                error: `Phase 2 failed: ${phase2Error.message || 'Unknown error'}`
                            }));
                        });
                        
                        // Continue to next batch
                        logger.info(`➡️  Continuing to next batch despite Phase 2 failure...`);
                        continue;
                    }
                }
            }

          logger.info(`🚀 Starting upload for batch ${batchNumber}/${totalBatches} (${currentBatch.length} files)`);

            // ★ Store Phase 2 data for URL refresh capability
            const phase2RefreshData = filesToProcess.map(({ file, detail, idx }) => {
                const initData = uploadInitDataArray[idx];
                const filePath = file.relativePath || file.name;
                const phase2Entry = (filesForPhase2 || []).find(f => f.file_path === filePath);
                return {
                    file_name: file.name,
                    extension: phase2Entry?.extension || '',
                    file_size: file.size || (file.file || file).size,
                    file_path: filePath,
                    content_type: (file.file || file).type || 'application/octet-stream',
                    upload_id: initData?.upload_id || detail?.upload_id,
                    parts: phase2Entry?.parts || []
                };
            });

            // Completion queue and tracking (persist across refresh attempts)
            const completionQueue = [];
            const completedFileIds = new Set();
            let urlsExpired = false;

            // ★ URL EXPIRY RETRY LOOP
            for (let refreshAttempt = 0; refreshAttempt <= MAX_URL_REFRESHES; refreshAttempt++) {
                urlsExpired = false;
                let fileIndex = 0;
                let batchWasAborted = false;

                if (refreshAttempt > 0) {
                    logger.info(`🔄 URL refresh attempt ${refreshAttempt}/${MAX_URL_REFRESHES} — restarting upload workers`);
                }

                // Helper function to check abort status in file worker
                const checkFileWorkerAbort = (abortSignal, uploadId, batchNumber, totalBatches, batchWasAborted) => {
                    if (!abortSignal?.aborted) return { shouldBreak: false, batchWasAborted };
                    const currentState = store.getState();
                    const currentUpload = currentState.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
                    const isPaused = currentUpload && currentUpload.status === UPLOAD_STATUS.PAUSED;
                    if (isPaused) {
                        logger.info(`⏸️  File worker stopped - upload paused (batch ${batchNumber}/${totalBatches})`);
                    } else {
                        logger.info(`🛑 File worker stopped - upload aborted (batch ${batchNumber}/${totalBatches})`);
                        batchWasAborted = true;
                    }
                    return { shouldBreak: true, batchWasAborted };
                };

                const findInitData = (uploadInitDataArray, filePath, fileName, file, batchNumber, totalBatches, uploadId, dispatch) => {
                    const initData = uploadInitDataArray.find(data => data.local_file_path === filePath);
                    if (!initData) {
                        console.error(`❌ [Batch ${batchNumber}/${totalBatches}] No upload initialization data found for file: ${filePath}`);
                        dispatch(updateFileStatus({
                            uploadId, fileId: file.id, status: UPLOAD_STATUS.FAILED,
                            error: `No upload initialization data found for file: ${filePath}`
                        }));
                        return null;
                    }
                    return initData;
                };

                const validateServerUploadState = (initData, file) => {
                    const { key, upload_id: s3UploadId, part_uploads, chunk_size, start_part = 1,
                        existing_parts = [], is_already_uploaded = false, uploaded_bytes = 0,
                        is_resume = false, checksum_algorithm, fields, headers } = initData;
                    const contentType = fields?.['Content-Type'] || '';
                    const kmsHeaders = headers || {};
                    let safeUploadedBytes = uploaded_bytes || 0;
                    if (safeUploadedBytes < 0) safeUploadedBytes = 0;
                    if (safeUploadedBytes > file.size) safeUploadedBytes = file.size;
                    return { key, s3UploadId, part_uploads, chunk_size, start_part, existing_parts,
                        is_already_uploaded, is_resume, checksum_algorithm, contentType, kmsHeaders, safeUploadedBytes };
                };

                const handleAlreadyUploaded = (file, uploadId, dispatch) => {
                    logger.info(`✅ File "${file.name}" is already uploaded on server. Skipping.`);
                    dispatch(updateFileStatus({ uploadId, fileId: file.id, status: UPLOAD_STATUS.COMPLETED }));
                    dispatch(updateFileProgress({ uploadId, fileId: file.id, progress: 100, uploadedBytes: file.size }));
                    return true;
                };

                const setupFileUpload = (file, uploadId, dispatch, is_resume, safeUploadedBytes, uploaded_bytes, s3UploadId, key, part_uploads, existing_parts, chunk_size, start_part) => {
                    if (is_resume) {
                        logger.info(`🔄 Resuming file "${file.name}" from part ${start_part}. Already uploaded: ${safeUploadedBytes} bytes`);
                    } else {
                        logger.info(`🆕 Starting fresh upload for "${file.name}"`);
                    }
                    dispatch(updateFileStatus({ uploadId, fileId: file.id, status: UPLOAD_STATUS.UPLOADING }));
                    addFileSessionData(uploadId, { key, upload_id: s3UploadId });
                    const partsMap = {};
                    existing_parts.forEach(({ part_number, etag, checksum }) => { partsMap[part_number] = { etag, checksum }; });
                    const partUploadsMap = {};
                    if (part_uploads && part_uploads.length > 0) { part_uploads.forEach(pu => { partUploadsMap[pu.part_number] = pu; }); }
                    const chunks = splitFileIntoChunks(file.file, chunk_size);
                    logger.info(`🔢 Chunk calculation for "${file.name}":`, { totalChunks: chunks.length, chunkSize: chunk_size, fileSize: file.size, existingPartsCount: existing_parts.length });
                    return { partsMap, partUploadsMap, chunks };
                };

                const syncInitialProgress = (uploadedBytes, file, uploadId, dispatch, chunks, partsMap, partUploadsMap, s3UploadId) => {
                    if (uploadedBytes > 0) {
                        const initialProgress = Math.round((uploadedBytes / file.size) * 100);
                        dispatch(updateFileProgress({ uploadId, fileId: file.id, progress: initialProgress, uploadedBytes }));
                    }
                };

                const handleCompletionQueue = (file, s3UploadId, key, partsMap, uploadedBytes, completionQueue) => {
                    const etagsPayload = Object.entries(partsMap)
                        .sort(([a], [b]) => a - b)
                        .map(([num, partData]) => {
                            if (typeof partData === 'string') return { part_number: +num, etag: partData };
                            const payload = { part_number: +num, etag: partData.etag };
                            if (partData.checksum) payload.checksum = partData.checksum;
                            return payload;
                        });
                    logger.info(`✅ File "${file.name}" chunks uploaded, queuing for batch-complete:`, { totalParts: etagsPayload.length, key });
                    completionQueue.push({ upload_id: s3UploadId, key, etags: etagsPayload, _fileId: file.id, _fileName: file.name });
                };

                const handleUploadError = (error, file, uploadId, dispatch, abortSignal, lockedFiles, wasAborted) => {
                    if (error.name === 'AbortError' || abortSignal?.aborted) {
                        const currentState = store.getState();
                        const currentUpload = currentState.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
                        const isPaused = currentUpload && currentUpload.status === UPLOAD_STATUS.PAUSED;
                        if (isPaused) return { shouldContinue: true, wasAborted };
                        return { shouldContinue: true, wasAborted: true };
                    }
                    if (isFileLockError(error)) lockedFiles.push(file.name);
                    const state = store.getState();
                    const upload = state.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
                    const fileState = upload?.files.find(f => f.id === file.id);
                    const hasPartialProgress = fileState && fileState.uploadedBytes > 0;
                    dispatch(updateFileStatus({
                        uploadId, fileId: file.id,
                        status: hasPartialProgress ? UPLOAD_STATUS.PARTIALLY_FAILED : UPLOAD_STATUS.FAILED,
                        error: error.message || 'Upload failed'
                    }));
                    return { shouldContinue: false, wasAborted };
                };

                // eslint-disable-next-line no-loop-func
                const fileWorker = async () => {
                    while (true) {
                        const abortCheck = checkFileWorkerAbort(abortSignal, uploadId, batchNumber, totalBatches, batchWasAborted);
                        if (abortCheck.shouldBreak) { batchWasAborted = abortCheck.batchWasAborted; break; }

                        // ★ Stop if URLs expired (another worker detected it)
                        if (urlsExpired) break;

                        const i = fileIndex++;
                        if (i >= currentBatch.length) break;

                        const file = currentBatch[i];

                        // ★ Skip files already completed in previous refresh attempt
                        if (completedFileIds.has(file.id)) continue;

                        const filePath = file.relativePath || file.name;
                        const fileName = file.name;

                        const initData = findInitData(uploadInitDataArray, filePath, fileName, file, batchNumber, totalBatches, uploadId, dispatch);
                        if (!initData) continue;

                        try {
                            const serverState = validateServerUploadState(initData, file);
                            if (serverState.is_already_uploaded && handleAlreadyUploaded(file, uploadId, dispatch)) {
                                completedFileIds.add(file.id);
                                continue;
                            }

                            const uploadSetup = setupFileUpload(
                                file, uploadId, dispatch, serverState.is_resume,
                                serverState.safeUploadedBytes, serverState.uploaded_bytes || 0,
                                serverState.s3UploadId, serverState.key, serverState.part_uploads,
                                serverState.existing_parts, serverState.chunk_size, serverState.start_part
                            );

                            let uploadedBytes = serverState.safeUploadedBytes;
                            syncInitialProgress(uploadedBytes, file, uploadId, dispatch, uploadSetup.chunks, uploadSetup.partsMap, uploadSetup.partUploadsMap, serverState.s3UploadId);

                            let chunkIndex = 0;
                            let loggedFirstChunk = false;

                            const uploadChunkWithRetries = async (url, chunks, j, checksum, algorithm) => {
                                const maxChunkRetries = RETRY_CONFIG.enabled ? RETRY_CONFIG.maxRetries : 0;
                                let lastChunkError = null;
                                let chunkResult = null;

                                for (let attempt = 0; attempt <= maxChunkRetries; attempt++) {
                                    if (abortSignal?.aborted || urlsExpired) break;
                                    try {
                                        chunkResult = await dispatch(uploadChunk({
                                            url, chunk: chunks[j], checksum, algorithm,
                                            requiresChecksums: initData.requires_checksums,
                                            contentType: serverState.contentType,
                                            kmsHeaders: serverState.kmsHeaders,
                                            signal: abortSignal
                                        })).unwrap();
                                        lastChunkError = null;
                                        break;
                                    } catch (chunkErr) {
                                        lastChunkError = chunkErr;
                                        if (chunkErr.name === 'AbortError' || chunkErr.type === 'ABORTED' || abortSignal?.aborted) break;
                                        // ★ URL expired — throw immediately, don't retry dead URL
                                        if (isUrlExpiredError(chunkErr)) throw chunkErr;
                                        if (attempt < maxChunkRetries) {
                                            const delay = RETRY_CONFIG.baseDelay * Math.pow(2, attempt);
                                            logger.info(`🔄 Chunk ${j+1}/${chunks.length} of "${fileName}" failed (attempt ${attempt+1}/${maxChunkRetries+1}). Retrying in ${delay}ms...`);
                                            await new Promise(resolve => setTimeout(resolve, delay));
                                        }
                                    }
                                }
                                if (lastChunkError) throw lastChunkError;
                                return chunkResult;
                            };

                            const uploadWorker = async () => {
                                while (true) {
                                    if (abortSignal?.aborted) break;
                                    // ★ Check shared flag
                                    if (urlsExpired) break;

                                    const j = chunkIndex++;
                                    if (j >= uploadSetup.chunks.length) break;

                                    if (abortSignal?.aborted || urlsExpired) break;

                                    const partNumber = j + 1;

                                    // Skip parts already uploaded
                                    if (uploadSetup.partsMap[partNumber]) continue;

                                    // Get URL for this part
                                    const partUpload = uploadSetup.partUploadsMap[partNumber];
                                    if (!partUpload?.url) {
                                        console.error(`❌ No URL found for part ${partNumber} of ${fileName}`);
                                        throw new Error(`No URL found for part ${partNumber}`);
                                    }

                                    const url = partUpload.url;
                                    const checksum = partUpload.checksum;
                                    const algorithm = partUpload.algorithm || serverState.checksum_algorithm;

                                    if (!loggedFirstChunk) {
                                        logger.info(`🔗 URL mapping verification for "${fileName}":`, { partNumber, hasUrl: !!url, hasChecksum: !!checksum, algorithm });
                                        loggedFirstChunk = true;
                                    }

                                    try {
                                        const chunkResult = await uploadChunkWithRetries(url, uploadSetup.chunks, j, checksum, algorithm);

                                        // ★ Check after upload
                                        if (urlsExpired || abortSignal?.aborted) break;

                                        uploadSetup.partsMap[partNumber] = {
                                            etag: chunkResult.etag || chunkResult,
                                            checksum: chunkResult.checksum
                                        };

                                        uploadedBytes += uploadSetup.chunks[j].size;
                                        const progress = Math.round((uploadedBytes / file.size) * 100);
                                        dispatch(updateFileProgress({ uploadId, fileId: file.id, progress, uploadedBytes }));
                                        logger.debug(`✅ ${fileName} part ${partNumber}/${uploadSetup.chunks.length} done (${progress}%)`);

                                    } catch (err) {
                                        // ★ URL EXPIRED — set shared flag, stop ALL workers
                                        if (isUrlExpiredError(err)) {
                                            logger.warn(`🔑 URL expired: ${fileName} part ${partNumber} — stopping all workers`);
                                            urlsExpired = true;
                                            break;
                                        }
                                        if (err.name === 'AbortError' || err.type === 'ABORTED' || abortSignal?.aborted) break;
                                        console.error(`❌ UPLOAD CHUNK FAILED for ${fileName} part ${partNumber}/${uploadSetup.chunks.length}:`, err.message);
                                        throw err;
                                    }
                                }
                            };

                            // Run parallel chunk workers
                            await Promise.all(
                                Array(Math.min(MAX_PARALLEL_UPLOADS, uploadSetup.chunks.length))
                                    .fill()
                                    .map(() => uploadWorker())
                            );

                            // ★ Don't complete if URLs expired — will retry after refresh
                            if (urlsExpired) continue;
                            if (abortSignal?.aborted) continue;

                            // Check if all parts done
                            const allDone = uploadSetup.chunks.every((_, idx) => uploadSetup.partsMap[idx + 1]);
                            if (allDone) {
                                handleCompletionQueue(file, serverState.s3UploadId, serverState.key, uploadSetup.partsMap, uploadedBytes, completionQueue);
                                completedFileIds.add(file.id);
                            }

                        } catch (error) {
                            // ★ URL expiry at file level
                            if (isUrlExpiredError(error)) {
                                urlsExpired = true;
                                continue;
                            }
                            const errorResult = handleUploadError(error, file, uploadId, dispatch, abortSignal, lockedFiles, wasAborted);
                            wasAborted = errorResult.wasAborted;
                            continue;
                        }
                    }
                };

                // Run parallel file workers
                logger.info(`🚀 [Batch ${batchNumber}/${totalBatches}] Starting ${Math.min(MAX_PARALLEL_FILES, currentBatch.length)} parallel file workers`);
                await Promise.all(
                    Array(Math.min(MAX_PARALLEL_FILES, currentBatch.length))
                        .fill()
                        .map(() => fileWorker())
                );
                if (batchWasAborted) wasAborted = true;

                // ★ If URLs did NOT expire, done with upload phase for this batch
                if (!urlsExpired) break;

                // ★ URLs expired — check if we can refresh
                if (refreshAttempt >= MAX_URL_REFRESHES) {
                    logger.error(`❌ Max URL refresh attempts (${MAX_URL_REFRESHES}) reached`);
                    currentBatch.forEach(file => {
                        if (!completedFileIds.has(file.id)) {
                            dispatch(updateFileStatus({
                                uploadId,
                                fileId: file.id,
                                status: UPLOAD_STATUS.FAILED,
                                error: 'URL refresh attempts exhausted — presigned URLs kept expiring'
                            }));
                        }
                    });
                    break;
                }

                // ★ Refresh URLs for all incomplete files
                logger.info(`🔄 URLs expired — refreshing (attempt ${refreshAttempt + 1}/${MAX_URL_REFRESHES})...`);

                const filesNeedingRefresh = phase2RefreshData.filter(f => {
                    const fileObj = currentBatch.find(cb => (cb.relativePath || cb.name) === f.file_path);
                    return fileObj && !completedFileIds.has(fileObj.id);
                });

                if (filesNeedingRefresh.length === 0) {
                    logger.info(`✅ All files already completed — no refresh needed`);
                    break;
                }

                try {
                    const refreshResults = await refreshExpiredUrls(uploadId, filesNeedingRefresh, uploadContext, dispatch);

                    // Update uploadInitDataArray with fresh URLs from backend
                    const refreshArray = Array.isArray(refreshResults) ? refreshResults : [refreshResults];
                    refreshArray.forEach(result => {
                        const idx = uploadInitDataArray.findIndex(d => d.local_file_path === result.local_file_path);
                        if (idx !== -1) {
                            uploadInitDataArray[idx] = result;
                        } else {
                            uploadInitDataArray.push(result);
                        }
                    });

                    logger.info(`✅ URLs refreshed for ${refreshArray.length} file(s) — restarting upload workers`);
                } catch (refreshError) {
                    logger.error(`❌ URL refresh failed:`, refreshError);
                    currentBatch.forEach(file => {
                        if (!completedFileIds.has(file.id)) {
                            dispatch(updateFileStatus({
                                uploadId,
                                fileId: file.id,
                                status: UPLOAD_STATUS.FAILED,
                                error: `URL refresh failed: ${refreshError.message || 'Unknown error'}`
                            }));
                        }
                    });
                    break;
                }
            } // ★ END of URL expiry retry loop

            // --- BATCH COMPLETE: Complete all successfully uploaded files in one API call ---
            if (completionQueue.length > 0 && !wasAborted) {
                logger.info(`📦 Batch-complete: sending ${completionQueue.length} file(s) for completion`);

                try {
                    const uploadsPayload = completionQueue.map(({ upload_id, key, etags }) => ({
                        upload_id, key, etags
                    }));

                    const batchResult = await dispatch(batchCompleteUpload({
                        uploads: uploadsPayload
                    })).unwrap();

                    const succeeded = batchResult?.succeeded || 0;
                    const failed = batchResult?.failed || 0;
                    logger.info(`📦 Batch-complete result: ${succeeded} succeeded, ${failed} failed out of ${completionQueue.length} file(s)`);

                    const fileResults = batchResult?.results || [];
                    const resultsByUploadId = {};
                    fileResults.forEach(r => {
                        if (r.upload_id) resultsByUploadId[r.upload_id] = r;
                    });

                    completionQueue.forEach((entry) => {
                        const fileResult = resultsByUploadId[entry.upload_id];
                        if (fileResult && fileResult.success === false) {
                            logger.info(`❌ Batch-complete: file "${entry._fileName}" failed: ${fileResult.error || 'Unknown error'}`);
                            dispatch(updateFileStatus({
                                uploadId,
                                fileId: entry._fileId,
                                status: UPLOAD_STATUS.FAILED,
                                error: `Complete upload failed: ${fileResult.error || 'Unknown error'}`
                            }));
                        } else {
                            dispatch(updateFileStatus({
                                uploadId,
                                fileId: entry._fileId,
                                status: UPLOAD_STATUS.COMPLETED
                            }));
                        }
                    });

                } catch (batchCompleteError) {
                    console.error(`❌ Batch-complete API failed:`, batchCompleteError);
                    completionQueue.forEach(entry => {
                        dispatch(updateFileStatus({
                            uploadId,
                            fileId: entry._fileId,
                            status: UPLOAD_STATUS.FAILED,
                            error: `Batch complete failed: ${batchCompleteError.message || 'Unknown error'}`
                        }));
                    });
                }
            } else if (completionQueue.length > 0 && wasAborted) {
                logger.info(`🛑 Skipping batch-complete for ${completionQueue.length} file(s) - upload was aborted`);
            }

            logger.info(`\n✅ Batch ${batchNumber}/${totalBatches} COMPLETE - All ${currentBatch.length} files processed`);
            logger.info(`${'='.repeat(80)}\n`);
                            

            // Check if aborted between batches
            /* istanbul ignore next -- abort between batch iterations timing-dependent */
            const abortResult7 = checkAbortOrPause(abortSignal, uploadId, `after batch ${batchNumber}/${totalBatches} completion`);
            /* istanbul ignore next */
            if (abortResult7) {
                if (abortResult7.wasAborted) wasAborted = true;
                return abortResult7;
            }
        }

        logger.info(`\n${'🎉'.repeat(40)}`);
        logger.info(`🎉 ALL ${totalBatches} BATCHES COMPLETE - ${files.length} total files processed`);
        logger.info(`${'🎉'.repeat(40)}\n`);
        
        // 📊 LOG: Final upload summary after all batches processed
        const finalState = store.getState();
        const finalUpload = finalState.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
        if (finalUpload) {
            logger.info(`📊 Final Upload Summary for "${finalUpload.datasetName}":`, {
                uploadId,
                overallStatus: finalUpload.status,
                overallProgress: finalUpload.overallProgress,
                wasAborted,
                filesBreakdown: {
                    total: finalUpload.files.length,
                    completed: finalUpload.files.filter(f => f.status === 'completed').length,
                    failed: finalUpload.files.filter(f => f.status === 'failed').length,
                    partiallyFailed: finalUpload.files.filter(f => f.status === 'partially-failed').length,
                    paused: finalUpload.files.filter(f => f.status === 'paused').length,
                    pending: finalUpload.files.filter(f => f.status === 'pending').length,
                    uploading: finalUpload.files.filter(f => f.status === 'uploading').length
                },
                fileDetails: finalUpload.files.map(f => ({
                    name: f.name,
                    status: f.status,
                    progress: f.progress,
                    uploadedBytes: f.uploadedBytes,
                    size: f.size,
                    error: f.error
                }))
            });

            // Extra logging for file statuses and allFilesCompleted check
            finalUpload.files.forEach((f, idx) => {
                logger.debug(`[UPLOAD DEBUG] File #${idx + 1}: name="${f.name}", status="${f.status}", progress=${f.progress}, uploadedBytes=${f.uploadedBytes}`);
            });
            const allFilesCompleted = finalUpload.files.every(f => f.status === UPLOAD_STATUS.COMPLETED);
            logger.debug(`[UPLOAD DEBUG] allFilesCompleted=${allFilesCompleted}, wasAborted=${wasAborted}, datasetId=${datasetId}`);

            // Call complete dataset API if all files completed successfully or if aborted (UPLOAD_ABORTED)
            let shouldCompleteDataset = false;
            let datasetStatus = null;
            if (allFilesCompleted && !wasAborted && datasetId) {
                shouldCompleteDataset = true;
                datasetStatus = DATASET_STATUS.UPLOAD_COMPLETED;
            } else if (wasAborted && datasetId) {
                shouldCompleteDataset = true;
                datasetStatus = DATASET_STATUS.UPLOAD_ABORTED;
            }

            if (shouldCompleteDataset) {
                try {
                    // Get dataset name and contract name from Redux state
                    const state = store.getState();
                    const upload = state.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
                    const datasetName = upload?.datasetName || '';
                    const contractName = upload?.contract?.label || uploadContext.contractName || '';
                    const contractId = upload?.contract?.ContractID || uploadContext?.contractId;
                    logger.info(`✅ Marking dataset ${datasetId} as ${datasetStatus}`);
                    await dispatch(completeDatasetAPI({
                        datasetId: datasetId,
                        status: datasetStatus,
                        businessUnit: uploadContext.businessUnit,
                        project: uploadContext.project,
                        contractId: uploadContext.contractId,
                        dataProviderName: uploadContext.dataProviderName,
                        contractName: contractName,
                        datasetName: datasetName
                    })).unwrap();
                    logger.info(`✅ Dataset ${datasetId} marked as ${datasetStatus}`);

                    // ========== Build and dispatch summary BEFORE removing session ==========
                    const summaryUpload = store.getState().uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
                    if (summaryUpload) {
                        const summary = buildUploadSummary(summaryUpload, wasAborted);
                        dispatch(setCompletedUploadSummary(summary));
                        logger.info(`📊 Upload summary dispatched for modal:`, summary);
                    }

                    // --- PATCH: Remove upload session from Redux before refreshing ---
                    logger.info(`🗑️ Removing upload session ${uploadId} from Redux before refresh`);
                    dispatch(removeUploadSession({ uploadId }));
                    
                    // Now refresh uploaded files list after completion/abort
                    logger.info(`📋 Refreshing uploaded files list`);
                    // await dispatch(fetchUploadedFiles({ limit: 100, offset: 0 }));
                   if (contractId) {
                    await dispatch(fetchUploadedFiles({ 
                        limit: DEFAULT_PAGE_LIMIT, 
                        offset: 0,
                        contractId: contractId
                    }));
                }
                    // --- END PATCH ---
                } catch (error) {
                    console.error(`❌ Failed to mark dataset as completed/aborted:`, error);
                    // Don't fail the upload if this call fails - just log it
                }
            } else {
                if (!allFilesCompleted && !wasAborted) {
                    logger.info('ℹ️ Not all files completed - skipping dataset completion API');
                }
                if (!datasetId) {
                    console.warn('⚠️ datasetId missing - cannot call dataset completion API');
                }
            }
        }
         // Auto-retry logic for failed files (only at top level, not for nested retry calls)
        await handleAutoRetry(uploadId, datasetId, files, uploadContext, abortSignal, isRetryCall, wasAborted, lockedFiles);
        
        // Show notification if any files failed due to being locked
        if (lockedFiles.length > 0) {
            const fileList = lockedFiles.length <= 3 
                ? lockedFiles.join(', ')
                : `${lockedFiles.slice(0, 3).join(', ')} and ${lockedFiles.length - 3} more`;
            
            dispatch(addErrorNotification({
                message: `Failed to upload ${lockedFiles.length} file(s) because they are locked or inaccessible: ${fileList}. Please close any programs using these files and try again.`
            }));
            
            console.warn(`⚠️ Locked files notification shown for: ${lockedFiles.join(', ')}`);
        }
        
        // Return final status for cleanup decision
      /* istanbul ignore next -- finalUpload absence is tested but coverage tracks the return separately */
        return { 
            wasAborted,
            overallStatus: finalUpload?.status || (wasAborted ? DATASET_STATUS.UPLOAD_ABORTED : DATASET_STATUS.UPLOAD_COMPLETED)
        };
        /* istanbul ignore stop */
        
    } catch (error) {
        console.error('❌ Batch upload initialization failed:', error);
        
        // Get the dataset name for user-friendly error message
        const state = store.getState();
        const upload = state.uploadProgress.activeUploads.find(u => u.uploadId === uploadId);
        const datasetName = upload?.datasetName || 'Upload';
        
        // Set error message in Redux for notification display
        dispatch(setUploadError({
            uploadId,
            error: `Failed to start upload for "${datasetName}": ${error.message || 'Unable to initialize upload session'}`
        }));
        
        // If batch upload initialization fails, mark all files as failed
        files.forEach(file => {
            dispatch(updateFileStatus({
                uploadId,
                fileId: file.id,
                status: UPLOAD_STATUS.FAILED,
                error: error.message || 'Failed to initialize upload'
            }));
        });
        
        // Return status indicating failure so file objects are preserved for retry
        return {
            wasAborted: false,
            overallStatus: UPLOAD_STATUS.FAILED
        };
    }
};

// Start background upload orchestration
export const startBackgroundUpload = (uploadId, datasetId, files, uploadContext) => {
    logger.info(`🚀 startBackgroundUpload called with:`, {
        uploadId,
        datasetId,
        filesCount: files.length,
        files: files.map(f => ({ id: f.id, name: f.name, hasFile: !!f.file })),
        uploadContext
    });
    
    // Create AbortController for this upload
    const abortController = new AbortController();
    
    // Store the AbortController so it can be accessed later to cancel the upload
    setUploadAbortController(uploadId, abortController);
    
    // Store File objects so they can be accessed on retry
    setUploadFileObjects(uploadId, files);
    
    logger.info(`🚀 Starting background upload with uploadId: ${uploadId}`);
    
    // Run orchestration in background (non-blocking)
    orchestrateUpload(uploadId, datasetId, files, uploadContext, abortController.signal)
        .then((result) => {
            // Clean up abort controller
            removeUploadAbortController(uploadId);
            
            logger.info(`🔍 Result object for upload ${uploadId}:`, {
                hasResult: !!result,
                wasAborted: result?.wasAborted,
                overallStatus: result?.overallStatus,
                fullResult: result
            });

             // ========== FIX: If this orchestration ended due to PAUSE, do NOTHING ==========
            // When user pauses and resumes, a NEW orchestrateUpload is started.
            // This .then() from the OLD run should not dispatch summaries or clean up.
            if (result?.overallStatus === UPLOAD_STATUS.PAUSED) {
                logger.info(`⏸️ Upload ${uploadId} was paused — skipping .then() processing (new orchestration will handle it)`);
                return;
            }
            
            // Check Redux status to distinguish pause from abort/failure
            const reduxState = store.getState();
            const currentUpload = reduxState.uploadProgress?.activeUploads?.find(u => u.uploadId === uploadId);
            const currentStatus = currentUpload?.status;
            
            if (!currentUpload) {
                console.warn(`⚠️ No Redux upload found for uploadId: ${uploadId}`);
                logger.info('Available uploads:', reduxState.uploadProgress?.activeUploads?.map(u => u.uploadId) || []);
            }
             if (currentStatus === UPLOAD_STATUS.IN_PROGRESS) {
                logger.info(`ℹ️ Upload ${uploadId} is currently IN_PROGRESS (likely resumed) — skipping old .then() processing`);
                return;
            }

            // Check if summary was already dispatched inside orchestrateUpload (after completeDatasetAPI)
            const existingSummary = reduxState.uploadProgress?.completedUploadSummary;
            const summaryAlreadyDispatched = existingSummary && existingSummary.uploadId === uploadId;

            if (summaryAlreadyDispatched) {
                logger.info(`ℹ️ Upload summary for ${uploadId} already dispatched inside orchestrateUpload — skipping .then() dispatch`);
            } else if (currentUpload && currentStatus !== UPLOAD_STATUS.PAUSED && result?.overallStatus !== UPLOAD_STATUS.PAUSED) {
                // Fallback path — build and dispatch summary here
                const summary = buildUploadSummary(currentUpload, result?.wasAborted);
                store.dispatch(setCompletedUploadSummary(summary));
                logger.info(`📊 Upload summary dispatched for modal (fallback path):`, summary);
            } else if (!currentUpload && !summaryAlreadyDispatched) {
                // Upload was removed but summary wasn't dispatched — this shouldn't happen
                // but log it for debugging
                logger.warn(`⚠️ Upload ${uploadId} removed from Redux but no summary was dispatched`);
            }
            
            // Only remove file objects and session data if upload completed successfully or was truly aborted
            // Keep them for pause, retry, failed, or partially-failed
            if (currentStatus === UPLOAD_STATUS.PAUSED || 
                result?.overallStatus === UPLOAD_STATUS.FAILED || 
                result?.overallStatus === UPLOAD_STATUS.PARTIALLY_FAILED) {
                logger.info(`⏸️ Upload ${uploadId} status: ${currentStatus || result.overallStatus} - keeping file objects and session data for resume/retry`);
            } else if (result?.wasAborted) {
                // True abort (not pause) - cleanup happens in handleAbortUpload
                logger.info(`🛑 Upload ${uploadId} was aborted - cleanup handled by handleAbortUpload`);
            } else {
                // Successful completion - cleanup after completion API
                removeUploadFileObjects(uploadId);
                removeUploadSessionData(uploadId);
                removeUploadContext(uploadId);
                logger.info(`✅ Upload ${uploadId} ${result?.overallStatus || 'completed'} - cleaning up file objects and context`);
            }
        })
        .catch(error => {
            // Clean up abort controller on error (but keep file objects and session data for retry)
            removeUploadAbortController(uploadId);
            console.error('Background upload orchestration failed:', error);
            logger.info(`⚠️ Upload ${uploadId} failed - keeping file objects and session data for retry`);
        });
};
